/**
 * Personel Dashboard — Faz 3.4 ile Anlık Alacak kartı eklendi.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  LogOut, Building2, Clock, MapPin, AlertCircle,
  QrCode, CheckCircle2, Calendar, Sun, ShieldCheck,
} from 'lucide-react'
import { QrScannerModal } from '@/components/qr/QrScannerModal'
import { IzinKarti } from '@/components/izin/IzinKarti'
import { suAnkiKonum, yaricapIcindeMi } from '@/lib/gps'
import { subeQrParse } from '@/lib/sube-qr'
import {
  bugunkuKayitlar, bugunDakika, icerideMi, kayitOlustur, sonKayitGetir,
  type Kayit,
} from '@/lib/services/kayit-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { personelGelecekVardiyalariGetir, type Vardiya } from '@/lib/services/vardiya-service'
import { DogumGunuKutlamasi } from '@/components/dogum/DogumGunuKutlamasi'
import { bugunDogumGunuMu } from '@/lib/services/dogum-service'
import { AvansKarti } from '@/components/avans/AvansKarti'
import { GorevlerimKarti } from '@/components/gorev/GorevlerimKarti'
import { BildirimZili } from '@/components/bildirim/BildirimZili'
import { AlacakKarti } from '@/components/maas/AlacakKarti'
import { tarihParse, GUN_ADLARI_UZUN, bugunMu, pazartesi, tarihStr } from '@/lib/tarih'
import type { KullaniciPersonel, Sube, Pozisyon } from '@/types'

type IslemDurumu =
  | { tip: 'hazir' }
  | { tip: 'qr-okuyor' }
  | { tip: 'gps-aliyor' }
  | { tip: 'kaydediyor' }
  | { tip: 'basarili'; mesaj: string }
  | { tip: 'hata'; mesaj: string }

export function PersonelDashboard() {
  const { kullanici, cikisYap } = useAuth()
  const personel = kullanici as KullaniciPersonel | null
  const profilFotoUrl = (personel as (KullaniciPersonel & { profilFotoUrl?: string }) | null)?.profilFotoUrl ?? null

  const [icer, setIcer] = useState<boolean | null>(null)
  const [sonKayit, setSonKayit] = useState<Kayit | null>(null)
  const [dogumGunuAcik, setDogumGunuAcik] = useState(false)
  const [bugunMin, setBugunMin] = useState<number>(0)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [subeler, setSubeler] = useState<Map<string, Sube>>(new Map())
  const [pozisyonlar, setPozisyonlar] = useState<Map<string, Pozisyon>>(new Map())
  const [vardiyalar, setVardiyalar] = useState<Vardiya[]>([])

  const [scannerAcik, setScannerAcik] = useState(false)
  const [islem, setIslem] = useState<IslemDurumu>({ tip: 'hazir' })

  const verileriYenile = useCallback(async () => {
    if (!personel) return
    try {
      const [icerideMidir, son, bugun, subeListesi, pozListesi, vardiyaList] =
        await Promise.all([
          icerideMi(personel.id),
          sonKayitGetir(personel.id),
          bugunkuKayitlar(personel.id),
          tumSubeleriGetir(),
          tumPozisyonlariGetir(),
          personelGelecekVardiyalariGetir(personel.id),
        ])
      setIcer(icerideMidir)
      setSonKayit(son)
      setBugunMin(bugunDakika(bugun))
      const sm = new Map<string, Sube>()
      subeListesi.forEach((s) => sm.set(s.id, s))
      setSubeler(sm)
      const pm = new Map<string, Pozisyon>()
      pozListesi.forEach((p) => pm.set(p.id, p))
      setPozisyonlar(pm)
      setVardiyalar(vardiyaList)
    } catch (err) {
      console.error(err)
    } finally {
      setYukleniyor(false)
    }
  }, [personel])

  useEffect(() => {
    verileriYenile()
  }, [verileriYenile])

// Doğum günü kontrolü — günde 1 kez göster
  useEffect(() => {
    if (!personel?.dogumTarihi) return
    if (!bugunDogumGunuMu(personel.dogumTarihi)) return
    const bugunIso = new Date().toISOString().substring(0, 10)
    const key = `dgKutlama_${personel.id}_${bugunIso}`
    if (localStorage.getItem(key)) return
    setDogumGunuAcik(true)
  }, [personel])

  useEffect(() => {
    if (!icer) return
    const interval = setInterval(() => verileriYenile(), 60000)
    return () => clearInterval(interval)
  }, [icer, verileriYenile])

  const haftalik = useMemo(() => {
    const buHafta = tarihStr(pazartesi(new Date()))
    const liste = vardiyalar.filter((v) => v.tarih >= tarihStr(new Date()))
    const buHaftaList = liste.filter((v) => v.haftaBaslangic === buHafta)
    const gelecekList = liste.filter((v) => v.haftaBaslangic > buHafta)
    return { buHafta: buHaftaList, gelecek: gelecekList }
  }, [vardiyalar])

  async function qrIslendi(qrIcerik: string) {
    setScannerAcik(false)
    if (!personel) {
      setIslem({ tip: 'hata', mesaj: 'Oturum bulunamadı.' })
      return
    }
    const parse = subeQrParse(qrIcerik)
    if (!parse.basarili || !parse.subeId) {
      setIslem({ tip: 'hata', mesaj: parse.hata ?? 'QR formatı geçersiz.' })
      return
    }
    const subeId = parse.subeId
    const sube = subeler.get(subeId)
    if (!sube) {
      setIslem({ tip: 'hata', mesaj: 'Bu şube sistemde bulunamadı.' })
      return
    }
    if (!personel.subeIdleri.includes(subeId)) {
      setIslem({ tip: 'hata', mesaj: `${sube.isim} şubende kayıtlı değilsin.` })
      return
    }
    setIslem({ tip: 'gps-aliyor' })
    let konum
    try {
      konum = await suAnkiKonum()
    } catch (err) {
      setIslem({ tip: 'hata', mesaj: (err as Error).message })
      return
    }
    if (!sube.konum?.lat || !sube.konum?.lng) {
      setIslem({ tip: 'hata', mesaj: 'Şubenin GPS konumu tanımsız.' })
      return
    }
    if (!yaricapIcindeMi(konum, { lat: sube.konum.lat, lng: sube.konum.lng })) {
      setIslem({ tip: 'hata', mesaj: `${sube.isim} şubesinden çok uzaksın.` })
      return
    }
    const yeniTip = icer ? 'cikis' : 'giris'
    setIslem({ tip: 'kaydediyor' })
    try {
      await kayitOlustur({
        personelId: personel.id, subeId, tip: yeniTip,
        yontem: 'qr', konum,
      })
      setIslem({
        tip: 'basarili',
        mesaj: yeniTip === 'giris'
          ? 'Giriş yapıldı — hayırlı mesailer! 💪'
          : 'Çıkış yapıldı — kolay gelsin! 👋',
      })
      verileriYenile()
      setTimeout(() => setIslem({ tip: 'hazir' }), 3000)
    } catch (err) {
      setIslem({ tip: 'hata', mesaj: 'Kayıt yazılamadı: ' + (err as Error).message })
    }
  }

  if (!personel) {
    return (
      <div className="page-container flex items-center justify-center">
        <p className="text-gray-500">Yükleniyor...</p>
      </div>
    )
  }

  const sonSube = sonKayit ? subeler.get(sonKayit.subeId) : null
  const saat = Math.floor(bugunMin / 60)
  const dakika = bugunMin % 60

  // Profil rozeti: pozisyon(lar) · şube(ler)
  const pozAdlari = (personel.pozisyonIdleri ?? [])
    .map((pid) => pozisyonlar.get(pid)?.isim)
    .filter(Boolean) as string[]
  const subeAdlari = (personel.subeIdleri ?? [])
    .map((sid) => subeler.get(sid)?.isim)
    .filter(Boolean) as string[]
  const rozetParcalari = [pozAdlari.join(', '), subeAdlari.join(', ')].filter(Boolean)
  const rozetMetni = rozetParcalari.join(' · ')

  return (
    <div className="page-container bg-gradient-to-br from-brand-50 via-white to-indigo-50 min-h-screen">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-2.5 flex items-center justify-end gap-1">
          {personel.yoneticiMi && (
            <Link
              to="/yonetici"
              className="flex items-center gap-1.5 px-3 py-1.5 mr-auto rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors text-sm font-medium"
              title="Yönetici Paneli"
            >
              <ShieldCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Yönetici Paneli</span>
            </Link>
          )}
          <BildirimZili kullaniciId={personel.id} />
          {personel.yoneticiMi && (
            <button onClick={cikisYap} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600" title="Çıkış">
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="flex flex-col items-center gap-3 pb-5 pt-1 px-4">
          {profilFotoUrl ? (
            <img
              src={profilFotoUrl}
              alt=""
              className="w-28 h-28 rounded-full object-cover border-[3px] border-white ring-1 ring-gray-200 shadow-sm"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-4xl font-bold border-[3px] border-white ring-1 ring-gray-200 shadow-sm">
              {(personel.isim ?? '?').trim().charAt(0).toUpperCase()}
            </div>
          )}
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900">{personel.isim}</p>
            {rozetMetni && (
              <span className="inline-block mt-1.5 text-xs bg-brand-50 text-brand-700 px-3 py-1 rounded-lg">
                {rozetMetni}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {yukleniyor ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : (
          <>
            <div className={`card ${
              icer ? 'bg-green-50 border-green-200'
                : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  icer ? 'bg-green-500 animate-pulse'
                    : 'bg-gray-400'
                }`} />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Şu an</p>
                  <p className="font-bold text-lg text-gray-900">
                    {icer ? 'Mesaidesin' : 'Mesai dışındasın'}
                  </p>
                  {icer && sonSube && (
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3" />
                      {sonSube.isim}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {islem.tip === 'gps-aliyor' && (
              <div className="card bg-blue-50 border-blue-200 text-center py-6">
                <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-2 animate-pulse" />
                <p className="font-semibold text-blue-900">Konum alınıyor...</p>
              </div>
            )}
            {islem.tip === 'kaydediyor' && (
              <div className="card bg-blue-50 border-blue-200 text-center py-6">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="font-semibold text-blue-900 mt-2">Kaydediliyor...</p>
              </div>
            )}
            {islem.tip === 'basarili' && (
              <div className="card bg-green-50 border-green-300 text-center py-6 animate-slide-up">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-green-900">{islem.mesaj}</p>
              </div>
            )}
            {islem.tip === 'hata' && (
              <div className="card bg-red-50 border-red-300">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-900 text-sm">{islem.mesaj}</p>
                    <button onClick={() => setIslem({ tip: 'hazir' })} className="text-xs text-red-700 underline mt-1">
                      Tamam
                    </button>
                  </div>
                </div>
              </div>
            )}

            {islem.tip === 'hazir' && (
              <button
                onClick={() => setScannerAcik(true)}
                className={`w-full py-8 rounded-3xl font-bold text-lg shadow-lg transition-all active:scale-95 flex flex-col items-center gap-2 ${
                  icer
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                <QrCode className="w-12 h-12" />
                <span>{icer ? 'QR Oku — Çıkış Yap' : 'QR Oku — Giriş Yap'}</span>
              </button>
            )}

            {/* 💰 ANLIK ALACAK */}
            <AlacakKarti personel={personel} />

            <div className="card">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-brand-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Bugün toplam</p>
                  <p className="font-bold text-2xl text-gray-900">
                    {saat}<span className="text-gray-500 text-lg">s</span>{' '}
                    {dakika}<span className="text-gray-500 text-lg">dk</span>
                  </p>
                </div>
              </div>
            </div>

            <AvansKarti personel={personel} />
            <IzinKarti personel={personel} />
            <GorevlerimKarti personel={personel} />

            <div>
              <div className="flex items-center gap-2 mb-2 px-1">
                <Calendar className="w-4 h-4 text-gray-500" />
                <h2 className="font-bold text-gray-900 text-sm">Vardiyalarım</h2>
              </div>
              {haftalik.buHafta.length === 0 && haftalik.gelecek.length === 0 ? (
                <div className="card text-center text-sm text-gray-500 py-6">
                  Bu hafta veya gelecek hafta için yayınlanmış vardiyan yok.
                </div>
              ) : (
                <div className="space-y-3">
                  {haftalik.buHafta.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 px-1">Bu Hafta</p>
                      <div className="card divide-y divide-gray-100 p-0">
                        {haftalik.buHafta.map((v) => (
                          <VardiyaSatiri
                            key={v.id} vardiya={v}
                            sube={subeler.get(v.subeId)}
                            pozisyon={v.pozisyonId ? pozisyonlar.get(v.pozisyonId) : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {haftalik.gelecek.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 px-1">Gelecek Hafta</p>
                      <div className="card divide-y divide-gray-100 p-0">
                        {haftalik.gelecek.map((v) => (
                          <VardiyaSatiri
                            key={v.id} vardiya={v}
                            sube={subeler.get(v.subeId)}
                            pozisyon={v.pozisyonId ? pozisyonlar.get(v.pozisyonId) : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {sonKayit && (
              <div className="card">
                <p className="text-xs text-gray-500 mb-1">Son hareketin</p>
                <p className="text-sm text-gray-900">
                  <span className="font-semibold">
                    {sonKayit.tip === 'giris' ? '🟢 Giriş' : '🔴 Çıkış'}
                  </span>
                  {' • '}{sonSube?.isim ?? '?'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {sonKayit.zaman.toDate().toLocaleString('tr-TR', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              </div>
            )}

            <div className="text-center text-xs text-gray-400 pt-4">
              Faz 3.4: Anlık alacak eklendi.
            </div>
          </>
        )}
      </div>

      <DogumGunuKutlamasi
        personelId={personel.id}
        personelAdi={personel.isim}
        acik={dogumGunuAcik}
        onKapat={() => {
          setDogumGunuAcik(false)
          const bugunIso = new Date().toISOString().substring(0, 10)
          localStorage.setItem(`dgKutlama_${personel.id}_${bugunIso}`, '1')
        }}
      />

      <QrScannerModal
        acik={scannerAcik}
        onKapat={() => setScannerAcik(false)}
        onOkundu={qrIslendi}
      />
    </div>
  )
}

interface VardiyaSatiriProps {
  vardiya: Vardiya
  sube?: Sube
  pozisyon?: Pozisyon
}

function VardiyaSatiri({ vardiya, sube, pozisyon }: VardiyaSatiriProps) {
  const tarih = tarihParse(vardiya.tarih)
  const bugun = bugunMu(tarih)
  const gunAdi = GUN_ADLARI_UZUN[(tarih.getDay() + 6) % 7]
  const aylar = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']
  const tarihEtiketi = `${tarih.getDate()} ${aylar[tarih.getMonth()]}`
  const izinli = vardiya.durum === 'izinli'
  const gece = !izinli && vardiya.bitis.localeCompare(vardiya.baslangic) <= 0

  return (
    <div className={`p-3 ${bugun ? (izinli ? 'bg-amber-50' : 'bg-brand-50') : ''}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold text-sm ${
              bugun ? (izinli ? 'text-amber-700' : 'text-brand-700') : 'text-gray-900'
            }`}>
              {bugun ? '🟢 Bugün' : gunAdi}
            </span>
            <span className="text-xs text-gray-500">{tarihEtiketi}</span>
            {izinli && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] rounded-full inline-flex items-center gap-0.5">
                <Sun className="w-2.5 h-2.5" />
                İzinli
              </span>
            )}
            {gece && (
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded-full">🌙</span>
            )}
          </div>
          {izinli ? (
            <div className="text-xs text-amber-800 mt-0.5">☀️ Bu gün izinlisin</div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
              <span className="font-medium text-gray-900">
                {vardiya.baslangic} – {vardiya.bitis}
              </span>
              {pozisyon && (<><span>·</span><span>{pozisyon.isim}</span></>)}
              {sube && (<><span>·</span><span className="truncate">{sube.isim}</span></>)}
            </div>
          )}
          {vardiya.notlar && (
            <p className="text-xs text-gray-500 mt-0.5 italic">{vardiya.notlar}</p>
          )}
        </div>
      </div>
    </div>
  )
}
